import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  Field,
  HStack,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  InputField,
  Tabs,
  Textarea,
  type Tab,
} from '@themes/aegix';
import { toaster } from '../../ui/toaster';
import {
  parseSamlMetadataXml,
  type ParsedSamlMetadata,
} from '../../../services';

/**
 * Import IdP metadata into the SAML config draft.
 *
 * Replaces the previous in-page "IdP federation metadata" + "Manual fields"
 * sections in `SamlConfigDrawer`. The drawer renders a read-only listing
 * of Entity ID / Sign-On URL / signing cert; this dialog is the single
 * write path for those values, exposed via the **Import Metadata** button.
 *
 * Three tabs:
 *   - **Upload** — pick an `.xml` file, read it client-side, parse via
 *     `parseSamlMetadataXml`, populate the result.
 *   - **Paste** — paste XML into a textarea, click Parse.
 *   - **Entry** — manual fallback for IdPs that don't publish metadata
 *     XML. Three text inputs (Entity ID, Sign-On URL, X.509 cert).
 *
 * On Apply, the dialog closes and the parent's `onApply` receives the
 * parsed values which it merges into the form draft.
 */

export interface ImportMetadataApply {
  entity_id: string;
  sso_url: string;
  cert: string;
  /**
   * The raw IdP metadata XML the user uploaded / pasted. Empty when the
   * Entry tab was used (no XML to keep). Returned so the parent can
   * re-extract attribute values (e.g. group claim values for Site / Role
   * mapping prepopulation) when the configured claim name changes later
   * in the same session.
   */
  raw_xml: string;
}

export interface ImportMetadataDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (values: ImportMetadataApply) => void;
  initialValues?: Partial<ImportMetadataApply>;
}

type TabId = 'upload' | 'paste' | 'entry';

const TABS: Tab[] = [
  { id: 'upload', label: 'Upload' },
  { id: 'paste', label: 'Paste' },
  { id: 'entry', label: 'Entry' },
];

export function ImportMetadataDialog({
  isOpen,
  onClose,
  onApply,
  initialValues,
}: ImportMetadataDialogProps) {
  const [activeTab, setActiveTab] = useState<TabId>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [pastedXml, setPastedXml] = useState<string>('');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<ParsedSamlMetadata | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  // Manual-entry tab state (Entry).
  const [entryEntityId, setEntryEntityId] = useState(
    initialValues?.entity_id ?? '',
  );
  const [entrySsoUrl, setEntrySsoUrl] = useState(initialValues?.sso_url ?? '');
  const [entryCert, setEntryCert] = useState(initialValues?.cert ?? '');

  // Reset state on open so a re-open doesn't show stale parse output.
  useEffect(() => {
    if (!isOpen) return;
    setActiveTab('upload');
    setFileName(null);
    setPastedXml('');
    setParsing(false);
    setParsed(null);
    setParseError(null);
    setEntryEntityId(initialValues?.entity_id ?? '');
    setEntrySsoUrl(initialValues?.sso_url ?? '');
    setEntryCert(initialValues?.cert ?? '');
  }, [
    isOpen,
    initialValues?.entity_id,
    initialValues?.sso_url,
    initialValues?.cert,
  ]);

  const handleParseXml = async (xml: string) => {
    if (!xml.trim()) {
      setParseError('Paste or upload the IdP federation metadata XML first.');
      return;
    }
    setParsing(true);
    setParseError(null);
    try {
      const result = await parseSamlMetadataXml(xml);
      setParsed(result);
    } catch (err) {
      const message = (err as Error).message || 'Failed to parse metadata XML.';
      setParseError(message);
      setParsed(null);
      toaster.create({
        title: 'Metadata parse failed',
        description: message,
        type: 'error',
        duration: 6000,
      });
    } finally {
      setParsing(false);
    }
  };

  const handleFile = async (file: File) => {
    try {
      const text = await file.text();
      setFileName(file.name);
      setPastedXml(text);
      setParseError(null);
      setParsed(null);
      // Auto-parse on upload — the file format is unambiguous, no
      // reason to make the admin click another button.
      await handleParseXml(text);
    } catch (err) {
      toaster.create({
        title: 'File read failed',
        description: (err as Error).message,
        type: 'error',
        duration: 6000,
      });
    }
  };

  const canApplyParsed = parsed !== null && !parsing;
  const canApplyEntry =
    entryEntityId.trim().length > 0 &&
    entrySsoUrl.trim().length > 0 &&
    entryCert.trim().length > 0;
  const canApply = activeTab === 'entry' ? canApplyEntry : canApplyParsed;

  const handleApply = () => {
    if (activeTab === 'entry') {
      onApply({
        entity_id: entryEntityId.trim(),
        sso_url: entrySsoUrl.trim(),
        cert: entryCert.trim(),
        raw_xml: '',
      });
      onClose();
      return;
    }
    if (!parsed) return;
    onApply({
      entity_id: parsed.entity_id,
      sso_url: parsed.sso_url,
      cert: parsed.certs[0] ?? '',
      raw_xml: pastedXml,
    });
    onClose();
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) onClose();
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="700px"
            data-e2e="sso-import-metadata-dialog"
          >
            <Dialog.Header>
              <Dialog.Title>Import IdP metadata</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="stretch" gap={4}>
                <Tabs
                  tabs={TABS}
                  activeTab={activeTab}
                  onChange={(id) => {
                    setActiveTab(id as TabId);
                    setParseError(null);
                  }}
                />

                {activeTab === 'upload' && (
                  <VStack align="stretch" gap={3}>
                    <Field.Root>
                      <Field.Label htmlFor="import-metadata-file">
                        Metadata XML
                      </Field.Label>
                      <Field.HelperText fontSize="xs" color="gray.500" mb={2}>
                        Most IdPs (Okta, Entra ID, Google Workspace, Duo)
                        publish a SAML 2.0 metadata XML file. Tiaki extracts
                        Entity ID, Sign-On URL, and signing certificate
                        automatically.
                      </Field.HelperText>
                      <HStack gap={3}>
                        <ButtonComponent
                          type="button"
                          visual="secondary"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={parsing}
                        >
                          {fileName ? 'Change file' : 'Choose XML file…'}
                        </ButtonComponent>
                        <Text
                          fontSize="sm"
                          color={fileName ? 'text.primary' : 'gray.500'}
                          wordBreak="break-all"
                        >
                          {fileName ?? 'No file selected'}
                        </Text>
                      </HStack>
                      <input
                        ref={fileInputRef}
                        id="import-metadata-file"
                        type="file"
                        accept=".xml,application/xml,text/xml"
                        hidden
                        data-e2e="sso-import-metadata-file"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void handleFile(file);
                        }}
                      />
                    </Field.Root>
                  </VStack>
                )}

                {activeTab === 'paste' && (
                  <VStack align="stretch" gap={3}>
                    <Field.Root>
                      <Field.Label htmlFor="import-metadata-xml">
                        Metadata XML
                      </Field.Label>
                      <Textarea
                        id="import-metadata-xml"
                        data-e2e="sso-import-metadata-xml"
                        fontFamily="mono"
                        fontSize="xs"
                        rows={10}
                        placeholder='<EntityDescriptor xmlns="urn:oasis:names:tc:SAML:2.0:metadata" ...>'
                        value={pastedXml}
                        onChange={(e) => {
                          setPastedXml(e.target.value);
                          setParsed(null);
                          setParseError(null);
                        }}
                      />
                    </Field.Root>
                    <HStack>
                      <ButtonComponent
                        type="button"
                        visual="primary"
                        onClick={() => void handleParseXml(pastedXml)}
                        disabled={!pastedXml.trim() || parsing}
                        data-e2e="sso-import-metadata-parse"
                      >
                        {parsing ? 'Parsing…' : 'Parse'}
                      </ButtonComponent>
                    </HStack>
                  </VStack>
                )}

                {activeTab === 'entry' && (
                  <VStack align="stretch" gap={3}>
                    <Text fontSize="sm" color="gray.600">
                      For IdPs that don't publish metadata XML, paste the
                      three values directly.
                    </Text>
                    <Field.Root>
                      <Field.Label htmlFor="import-entity-id">
                        IdP Entity ID
                      </Field.Label>
                      <InputField
                        id="import-entity-id"
                        data-e2e="sso-import-entity-id"
                        value={entryEntityId}
                        onChange={(e) => setEntryEntityId(e.target.value)}
                      />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label htmlFor="import-sso-url">
                        IdP Single Sign-On URL
                      </Field.Label>
                      <InputField
                        id="import-sso-url"
                        data-e2e="sso-import-sso-url"
                        value={entrySsoUrl}
                        onChange={(e) => setEntrySsoUrl(e.target.value)}
                      />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label htmlFor="import-cert">
                        IdP signing certificate (X.509 base64)
                      </Field.Label>
                      <Field.HelperText fontSize="xs" color="gray.500" mb={2}>
                        BEGIN/END markers are stripped automatically.
                      </Field.HelperText>
                      <Textarea
                        id="import-cert"
                        data-e2e="sso-import-cert"
                        fontFamily="mono"
                        fontSize="xs"
                        rows={6}
                        placeholder="MIIDdzCCAl+gAwIBAgIE…"
                        value={entryCert}
                        onChange={(e) => setEntryCert(e.target.value)}
                      />
                    </Field.Root>
                  </VStack>
                )}

                {parseError && activeTab !== 'entry' ? (
                  <Box
                    borderWidth="1px"
                    borderColor="red.200"
                    borderRadius="md"
                    p={3}
                    data-e2e="sso-import-metadata-error"
                  >
                    <Text fontSize="sm" fontWeight={600} color="red.700" mb={1}>
                      Could not parse metadata XML
                    </Text>
                    <Text fontSize="sm" color="red.600">
                      {parseError} If your IdP doesn't publish metadata XML,
                      switch to the <strong>Entry</strong> tab and paste the
                      three values directly.
                    </Text>
                  </Box>
                ) : null}

                {parsed && activeTab !== 'entry' ? (
                  <Box
                    borderWidth="1px"
                    borderColor="green.200"
                    borderRadius="md"
                    p={3}
                    data-e2e="sso-import-metadata-preview"
                  >
                    <Text fontSize="sm" fontWeight={600} mb={2} color="green.700">
                      Parsed
                      {parsed.certs.length > 1
                        ? ` · ${parsed.certs.length} signing certs (rotation candidate detected)`
                        : ''}
                    </Text>
                    <VStack align="stretch" gap={1} fontSize="xs">
                      <Text>
                        <Text as="span" fontWeight={600}>
                          Entity ID:{' '}
                        </Text>
                        {parsed.entity_id}
                      </Text>
                      <Text>
                        <Text as="span" fontWeight={600}>
                          Sign-On URL:{' '}
                        </Text>
                        {parsed.sso_url}
                      </Text>
                      <Text fontFamily="mono" wordBreak="break-all">
                        <Text as="span" fontFamily="body" fontWeight={600}>
                          Cert:{' '}
                        </Text>
                        {(parsed.certs[0] ?? '').slice(0, 80)}…
                      </Text>
                    </VStack>
                  </Box>
                ) : null}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <ButtonComponent visual="secondary" onClick={onClose}>
                  Cancel
                </ButtonComponent>
                <ButtonComponent
                  visual="primary"
                  onClick={handleApply}
                  disabled={!canApply}
                  data-e2e="sso-import-metadata-apply"
                >
                  Import
                </ButtonComponent>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export default ImportMetadataDialog;
